import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Globe, ChevronDown, User, LogOut, LayoutDashboard } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Logo } from "./Logo";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export function Navbar() {
  const { t, lang, changeLang } = useLang();
  const { user, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: "/buy", label: t("nav.buy") },
    { to: "/rent", label: t("nav.rent") },
    { to: "/sell", label: t("nav.sell") },
    { to: "/neighborhoods", label: t("nav.neighborhoods") },
    { to: "/agents", label: t("nav.agents") },
    { to: "/journal", label: t("nav.journal") },
  ];

  const overHero = pathname === "/" && !scrolled;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "backdrop-blur-xl border-b border-primary/5 shadow-[0_10px_40px_rgba(11,20,45,0.06)]" : "bg-transparent"}`}
      data-testid="navbar"
    >
      {overHero && (
        <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/45 via-black/15 to-transparent -z-10" aria-hidden="true" />
      )}
      <div className={`max-w-[1440px] mx-auto px-5 lg:px-12 flex items-center justify-between transition-all duration-500 ${scrolled ? "h-[4.75rem]" : "h-24"}`}>
        <Link to="/" className="flex items-center gap-2.5 transition-transform duration-500 hover:scale-[1.03] active:scale-95" data-testid="brand-logo">
          <Logo variant={overHero ? "light" : "dark"} className={`transition-all duration-500 logo-glow ${scrolled ? "h-14 lg:h-16" : "h-16 lg:h-20"}`} />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`nav-link text-xs uppercase tracking-[0.16em] font-semibold transition-all duration-500 ${overHero ? "text-white hover:text-[#f0cf88] nav-glow" : "text-foreground/70 hover:text-primary"} ${pathname === l.to ? `active ${overHero ? "text-[#f0cf88]" : "text-primary"}` : ""}`}
              data-testid={`nav-${l.to.slice(1)}`}
            >
              {l.label}
            </Link>
          ))}
          <Link
            to="/virtual-tour"
            className="group flex items-center gap-2 rounded-full bg-brandgreen text-white px-5 py-2 text-xs uppercase tracking-[0.15em] font-medium pill-glow hover:bg-brandgreen/90 transition-all duration-500 active:scale-95"
            data-testid="nav-virtual-tour"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
            {t("nav.virtualTour")}
            <span className="text-[0.6rem] opacity-80">360°</span>
          </Link>
        </nav>

        <div className="flex items-center gap-5">
          <button
            onClick={() => changeLang(lang === "en" ? "es" : "en")}
            className={`hidden sm:flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-semibold transition-all duration-500 active:scale-95 ${overHero ? "text-white hover:text-[#f0cf88] nav-glow" : "text-foreground/70 hover:text-brandgreen"}`}
            data-testid="lang-toggle"
          >
            <Globe size={15} strokeWidth={1.5} /> {lang === "en" ? "EN" : "ES"}
          </button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className={`flex items-center gap-2 px-4 py-2.5 border transition-colors duration-500 active:scale-95 ${overHero ? "border-white/70 text-white nav-glow" : "border-primary/20 text-foreground hover:border-primary"}`} data-testid="user-menu">
                  <User size={16} strokeWidth={1.5} />
                  <span className="hidden sm:inline text-sm max-w-[110px] truncate">{user.name}</span>
                  <ChevronDown size={14} strokeWidth={1.5} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                  <LayoutDashboard size={15} className="mr-2" /> {t("nav.dashboard")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut size={15} className="mr-2" /> {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login" className={`btn-lux hidden sm:inline-flex items-center px-6 py-2.5 text-xs uppercase tracking-[0.2em] font-semibold transition-all duration-500 ${overHero ? "border border-white/70 text-white hover:bg-white hover:text-primary nav-glow" : "border border-primary text-primary hover:bg-primary hover:text-primary-foreground"}`} data-testid="signin-btn">
              {t("nav.signin")}
            </Link>
          )}

          <button className={`lg:hidden active:scale-90 transition-transform ${overHero ? "text-white" : "text-foreground"}`} onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X size={26} strokeWidth={1.5} /> : <Menu size={26} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden glass border-t border-border px-5 py-4 flex flex-col gap-1" data-testid="mobile-menu">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="py-3 text-lg text-foreground/80 hover:text-primary transition-colors">
              {l.label}
            </Link>
          ))}
          <button onClick={() => changeLang(lang === "en" ? "es" : "en")} className="py-3 text-lg text-left text-foreground/80 flex items-center gap-2">
            <Globe size={18} /> {lang === "en" ? "English" : "Español"}
          </button>
          <Link to="/virtual-tour" onClick={() => setOpen(false)} className="mt-1 mb-1 px-4 py-3 bg-brandgreen text-white text-center text-base flex items-center justify-center gap-2" data-testid="mobile-nav-virtual-tour">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> {t("nav.virtualTour")} <span className="text-xs opacity-80">360°</span>
          </Link>
          {!user && (
            <Link to="/login" onClick={() => setOpen(false)} className="mt-2 px-5 py-3.5 bg-primary text-primary-foreground text-center text-base">
              {t("nav.signin")}
            </Link>
          )}
        </div>
      )}
    </header>
  );
}
